import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { SearchX, BookmarkX, X, Loader2, Lock } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/catalog";
import Header from "@/components/dossier/Header";
import Hero from "@/components/dossier/Hero";
import Featured from "@/components/dossier/Featured";
import Controls from "@/components/dossier/Controls";
import ArticleCard from "@/components/dossier/ArticleCard";
import DossierTable from "@/components/dossier/DossierTable";
import ContinueStrip from "@/components/dossier/ContinueStrip";
import ThemesGallery from "@/components/dossier/ThemesGallery";
import About from "@/components/dossier/About";
import Footer from "@/components/dossier/Footer";

const LS_SAVED = "dl_reading_list";
const LS_OPENED = "dl_opened";
const LS_TOKEN = "dl_admin_token";

// First-time visitors start with a curated 5-dossier reading list (one per major theme).
const DEFAULT_READING_LIST = [
  "un-breakdown-media",
  "mb-infiltration-europe-uk",
  "third-century-translation-sin",
  "when-truth-dies",
  "european-ma-outlook-2025",
];

function App() {
  const [uiMode, setUiMode] = useState(() => document.documentElement.dataset.theme || "dark");
  useEffect(() => {
    const on = (e) => setUiMode(e.detail);
    window.addEventListener("dl-theme", on);
    return () => window.removeEventListener("dl-theme", on);
  }, []);
  const [author, setAuthor] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [articles, setArticles] = useState([]);
  const [sync, setSync] = useState(null);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [theme, setTheme] = useState("all");
  const [source, setSource] = useState("all");
  const [lang, setLang] = useState("all");
  const [savedOnly, setSavedOnly] = useState(false);
  const [view, setView] = useState("grid");

  const [saved, setSaved] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_SAVED);
      if (raw !== null) return JSON.parse(raw) || [];
      return DEFAULT_READING_LIST;
    } catch { return DEFAULT_READING_LIST; }
  });

  const [opened, setOpened] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_OPENED)) || []; } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(LS_OPENED, JSON.stringify(opened)); }, [opened]);
  const markOpened = useCallback((slug) => {
    setOpened((prev) => [{ slug, at: Date.now() }, ...prev.filter((o) => o.slug !== slug)].slice(0, 50));
  }, []);
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const hit = articles.find((x) => x.url === a.getAttribute("href"));
      if (hit) markOpened(hit.slug);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, [articles, markOpened]);
  const openedSlugs = useMemo(() => opened.map((o) => o.slug), [opened]);

  const [editorToken, setEditorToken] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [editorActive, setEditorActive] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [pass, setPass] = useState("");
  const [checking, setChecking] = useState(false);

  const searchRef = useRef(null);
  const subscribeRef = useRef(null);

  const loadData = useCallback(async () => {
    const [a, t, arts, s] = await Promise.all([
      api.author(), api.taxonomy(), api.articles(), api.syncStatus().catch(() => null),
    ]);
    setAuthor(a); setTaxonomy(t); setArticles(arts); setSync(s);
  }, []);

  useEffect(() => {
    loadData().catch(() => {}).finally(() => setLoading(false));
  }, [loadData]);

  // restore editor session
  useEffect(() => {
    if (editorToken) {
      api.adminCheck(editorToken).then(() => setEditorActive(true)).catch(() => {
        localStorage.removeItem(LS_TOKEN); setEditorToken("");
      });
    }
  }, [editorToken]);

  useEffect(() => { localStorage.setItem(LS_SAVED, JSON.stringify(saved)); }, [saved]);

  // Import a shared reading list from ?s=<code> (short) or ?list=slug,slug (legacy)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("s");
    const list = params.get("list");
    const apply = (slugs) => {
      const clean = (slugs || []).map((s) => s.trim()).filter(Boolean);
      if (clean.length) {
        setSaved((prev) => Array.from(new Set([...prev, ...clean])));
        setSavedOnly(true);
        toast.success(`Loaded a shared reading list · ${clean.length} dossiers`, { duration: 5000 });
        setTimeout(() => document.getElementById("index")?.scrollIntoView({ behavior: "smooth" }), 600);
      }
    };
    if (code) {
      api.getShareList(code)
        .then((r) => apply(r.slugs))
        .catch(() => toast.error("That share link could not be found"))
        .finally(() => window.history.replaceState({}, "", window.location.pathname));
    } else if (list) {
      apply(list.split(","));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const shareList = async () => {
    if (saved.length === 0) { toast("Save some dossiers first"); return; }
    const base = `${window.location.origin}${window.location.pathname}`;
    try {
      const { code } = await api.createShareList(saved);
      await navigator.clipboard.writeText(`${base}?s=${code}`);
      toast.success("Short share link copied — send it to anyone");
    } catch {
      try { await navigator.clipboard.writeText(`${base}?list=${saved.join(",")}`); } catch {}
      toast.success("Share link copied");
    }
  };

  const pickTheme = (id) => {
    setTheme(id);
    setSavedOnly(false);
    const el = document.getElementById("index");
    if (el) window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - 70), behavior: "smooth" });
  };

  const toggleSave = (slug) => {
    setSaved((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      toast.success(prev.includes(slug) ? "Removed from reading list" : "Saved to reading list");
      return next;
    });
  };

  const submitEditor = async (e) => {
    e.preventDefault();
    if (!pass.trim()) return;
    setChecking(true);
    try {
      await api.adminCheck(pass.trim());
      localStorage.setItem(LS_TOKEN, pass.trim());
      setEditorToken(pass.trim());
      setEditorActive(true);
      setShowEditorModal(false);
      setPass("");
      toast.success("Editor mode enabled");
    } catch {
      toast.error("Wrong passcode");
    } finally {
      setChecking(false);
    }
  };

  const onEditor = () => {
    if (editorActive) {
      setEditorActive(false);
      localStorage.removeItem(LS_TOKEN);
      setEditorToken("");
      toast("Editor mode off");
    } else {
      setShowEditorModal(true);
    }
  };

  const onPatch = async (slug, body) => {
    try {
      await api.patchArticle(slug, body, editorToken);
      await loadData();
      toast.success(body.theme ? "Theme updated" : (body.featured ? "Added to spotlight" : "Removed from spotlight"));
    } catch {
      toast.error("Update failed — check editor access");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      if (theme !== "all" && a.theme !== theme) return false;
      if (source !== "all" && a.source !== source) return false;
      if (lang !== "all" && (a.lang || "EN") !== lang) return false;
      if (savedOnly && !saved.includes(a.slug)) return false;
      if (q && !(`${a.title} ${a.excerpt}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [articles, query, theme, source, lang, savedOnly, saved]);

  const focusSearch = () => {
    document.getElementById("index")?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => searchRef.current?.focus(), 500);
  };
  const goSubscribe = () => subscribeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  const goReadingList = () => {
    setSavedOnly(true);
    document.getElementById("index")?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--ink)]">
        <div className="flex flex-col items-center gap-4">
          <span className="grid place-items-center w-12 h-12 border font-serif text-xl live-dot"
            style={{ borderColor: "var(--gold)", color: "var(--gold)" }}>DL</span>
          <span className="meta-label text-[var(--muted)]">Opening dossier…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      <Toaster theme={uiMode} position="bottom-right" toastOptions={{
        style: { background: "var(--surface-2)", border: "1px solid var(--line-2)", color: "var(--txt)", borderRadius: 0 },
      }} />
      <Header onSearchClick={focusSearch} onSubscribe={goSubscribe} onReadingList={goReadingList} savedCount={saved.length} />
      <Hero author={author} taxonomy={taxonomy} />
      <About author={author} />
      <ThemesGallery themes={taxonomy?.themes || []} onPick={pickTheme} />
      <Featured articles={articles} />
      <Controls
        taxonomy={taxonomy}
        query={query} setQuery={setQuery}
        theme={theme} setTheme={setTheme}
        source={source} setSource={setSource}
        lang={lang} setLang={setLang}
        savedOnly={savedOnly} setSavedOnly={setSavedOnly} savedCount={saved.length} onShare={shareList}
        sync={sync}
        view={view} setView={setView}
        resultCount={filtered.length}
        searchRef={searchRef}
      />

      <main className="max-w-[1320px] mx-auto px-5 sm:px-8 pt-4 pb-6 min-h-[40vh]">
        <ContinueStrip articles={articles} opened={opened} />
        {filtered.length === 0 ? (
          <div data-testid="empty-state" className="flex flex-col items-center justify-center py-24 text-center">
            {savedOnly ? <BookmarkX size={36} className="text-[var(--muted)]" /> : <SearchX size={36} className="text-[var(--muted)]" />}
            <p className="font-serif text-2xl mt-5 text-[var(--txt)]">
              {savedOnly ? "Your reading list is empty" : "No entries match that filter"}
            </p>
            <p className="meta-label text-[var(--muted)] mt-2">
              {savedOnly ? "Tap the bookmark on any dossier to save it here" : "Try clearing the search or switching theme"}
            </p>
            <button data-testid="reset-filters" onClick={() => { setQuery(""); setTheme("all"); setSource("all"); setLang("all"); setSavedOnly(false); }}
              className="mt-6 meta-label px-5 py-3 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors">
              {savedOnly ? "Browse all dossiers" : "Reset filters"}
            </button>
          </div>
        ) : view === "grid" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {filtered.map((a, i) => (
                <ArticleCard
                  key={a.slug} article={a} index={i}
                  saved={saved} onToggleSave={toggleSave} opened={openedSlugs.includes(a.slug)}
                  editor={editorActive} themes={taxonomy?.themes || []} onPatch={onPatch}
                />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <DossierTable articles={filtered} opened={openedSlugs} onOpen={markOpened} />
        )}
      </main>

      <Footer author={author} subscribeRef={subscribeRef} onEditor={onEditor} editorActive={editorActive} />

      <AnimatePresence>
        {showEditorModal && (
          <motion.div
            className="fixed inset-0 z-[60] grid place-items-center p-4"
            style={{ background: "rgba(var(--ink-rgb),0.8)", backdropFilter: "blur(6px)" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowEditorModal(false)}
          >
            <motion.form
              data-testid="editor-modal"
              onSubmit={submitEditor}
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-sm bg-[var(--surface)] border border-[var(--line-2)] p-7 relative"
            >
              <span className="absolute top-0 left-0 w-12 h-[2px] bg-[var(--gold)]" />
              <button type="button" onClick={() => setShowEditorModal(false)} className="absolute top-4 right-4 text-[var(--muted)] hover:text-[var(--gold)]"><X size={18} /></button>
              <div className="flex items-center gap-2 text-[var(--gold)]"><Lock size={15} /><p className="meta-label">Editor Access</p></div>
              <h3 className="font-serif text-2xl mt-3 text-[var(--txt)]">Enter your passcode</h3>
              <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">Editors can re-tag themes and pick which essays headline the spotlight.</p>
              <input
                data-testid="editor-passcode-input"
                type="password" value={pass} onChange={(e) => setPass(e.target.value)} autoFocus
                placeholder="Passcode"
                className="w-full mt-5 bg-[var(--ink)] border border-[var(--line-2)] focus:border-[var(--gold)] outline-none px-4 py-3 text-sm text-[var(--txt)] placeholder:text-[var(--muted)]"
              />
              <button data-testid="editor-submit-btn" type="submit" disabled={checking}
                className="w-full mt-4 flex items-center justify-center gap-2 py-3 meta-label disabled:opacity-60"
                style={{ backgroundColor: "var(--gold)", color: "var(--ink)" }}>
                {checking ? <Loader2 size={15} className="animate-spin" /> : "Unlock editor"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
